import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, DollarSign } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, toDateInput, fromDateInput } from "@/lib/api";

const INVOICE_TYPES = [
  { id: "FACTURA", label: "Factura (consumidor final)" },
  { id: "CCF", label: "Comprobante de Crédito Fiscal (CCF)" },
];

export default function InvoiceDialog({ open, onOpenChange, deal, quote }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => ({
    documentType: "FACTURA",
    amountTotal: quote?.total ?? (deal?.dealValue || 0),
    issuedAt: new Date().toISOString(),
    dueAt: null,
    pdfLink: "",
    xmlLink: "",
    notes: "",
  }));

  const create = useMutation({
    mutationFn: async () => {
      const body = {
        dealId: deal.id,
        quoteId: quote?.id,
        documentType: form.documentType,
        amountTotal: Number(form.amountTotal) || 0,
        issuedAt: form.issuedAt,
        dueAt: form.dueAt,
        pdfLink: form.pdfLink,
        xmlLink: form.xmlLink,
        notes: form.notes,
      };
      return (await api.post("/invoices", body)).data;
    },
    onSuccess: (inv) => {
      toast.success(`Factura ${inv.invoiceNumber} emitida`);
      qc.invalidateQueries({ queryKey: ["invoices", deal.id] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["crm-revenue"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-card" data-testid="invoice-dialog">
        <DialogHeader>
          <div className="kicker">Nueva factura</div>
          <DialogTitle className="font-display text-3xl font-normal">Emitir factura · {deal.clientName}</DialogTitle>
          <DialogDescription>
            Fase A: registra manualmente la factura (puedes pegar enlaces a PDF/XML en Drive).
            La emisión automática por SmartBill se activa cuando conectes la integración.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label className="text-xs">Tipo de documento</Label>
            <Select value={form.documentType} onValueChange={(v) => set("documentType", v)}>
              <SelectTrigger data-testid="invoice-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{INVOICE_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label className="text-xs">Monto total (USD)</Label>
              <Input type="number" min="0" step="0.01" value={form.amountTotal} onChange={(e) => set("amountTotal", e.target.value)} className="font-mono" data-testid="invoice-amount-input" /></div>
            <div className="grid gap-1.5"><Label className="text-xs">Fecha de emisión</Label>
              <Input type="date" value={toDateInput(form.issuedAt)} onChange={(e) => set("issuedAt", fromDateInput(e.target.value))} data-testid="invoice-issued-input" /></div>
            <div className="grid gap-1.5"><Label className="text-xs">Fecha de vencimiento</Label>
              <Input type="date" value={toDateInput(form.dueAt)} onChange={(e) => set("dueAt", fromDateInput(e.target.value))} data-testid="invoice-due-input" /></div>
          </div>
          <div className="grid gap-1.5"><Label className="text-xs">Enlace al PDF (Drive, Dropbox…)</Label>
            <Input type="url" value={form.pdfLink} onChange={(e) => set("pdfLink", e.target.value)} placeholder="https://drive.google.com/..." className="font-mono text-xs" data-testid="invoice-pdf-link" /></div>
          <div className="grid gap-1.5"><Label className="text-xs">Enlace al XML (Hacienda / DTE)</Label>
            <Input type="url" value={form.xmlLink} onChange={(e) => set("xmlLink", e.target.value)} placeholder="https://..." className="font-mono text-xs" data-testid="invoice-xml-link" /></div>
          <div className="grid gap-1.5"><Label className="text-xs">Notas</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="invoice-notes-input" /></div>
        </div>
        <Button onClick={() => create.mutate()} disabled={create.isPending || !form.amountTotal} data-testid="invoice-create-button">
          {create.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <DollarSign className="mr-1.5 h-4 w-4" />}
          Registrar factura
        </Button>
      </DialogContent>
    </Dialog>
  );
}
